import { useLoad } from "@redotech/react-util/load";
import type { LocationId } from "@redotech/redo-ids/ids";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCallback, useContext, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

const DEVICE_LOCATION_KEY = "redo:deviceLocationId";
const WMS_LOCATION_STORAGE_KEY = "wms-selected-location-id";

export function getDeviceLocationId(): LocationId | undefined {
  const device = localStorage.getItem(DEVICE_LOCATION_KEY);
  if (device) {
    return device as LocationId;
  }
  const wms = localStorage.getItem(WMS_LOCATION_STORAGE_KEY);
  if (wms) {
    return wms as LocationId;
  }
  return undefined;
}

export function setDeviceLocationId(locationId: LocationId | undefined): void {
  if (locationId) {
    localStorage.setItem(DEVICE_LOCATION_KEY, locationId);
  } else {
    localStorage.removeItem(DEVICE_LOCATION_KEY);
  }
}

export function useDeviceLocation(): [
  LocationId | undefined,
  (locationId: LocationId | undefined) => void,
] {
  const team = useContext(TeamContext);
  const rpcClient = useContext(RedoOutboundLabelsRpcClientContext);

  const [locationId, setLocationId] = useState<LocationId | undefined>(
    getDeviceLocationId,
  );

  const defaultExternalId =
    team?.settings?.outboundLabels?.wms?.defaultFulfillmentLocationExternalId;

  const defaultLoad = useLoad(
    async (signal) => {
      if (locationId || !rpcClient || !defaultExternalId) {
        return undefined;
      }
      const { locations } = await rpcClient.getCrdbLocations(null, { signal });
      const match = locations.find(
        (loc) => loc.externalId === defaultExternalId,
      );
      return (match?.id ?? locations[0]?.id ?? undefined) as
        | LocationId
        | undefined;
    },
    [locationId, rpcClient, defaultExternalId],
  );

  const update = useCallback((id: LocationId | undefined) => {
    setDeviceLocationId(id);
    setLocationId(id);
  }, []);

  return [locationId ?? defaultLoad.value ?? undefined, update];
}
